import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError } from "../services/apiClient";
import {
  applyTaggedPlaceholderDisplayValues,
  migrateBareDoubleBracketTokensToContentControls,
  unwrapTaggedPlaceholderControlsPreserveContent
} from "../services/doubleBracketWordService";
import { getDraftFileBase64 } from "../services/generatedContractService";
import {
  commitGameRuleGenerationSession,
  generationSessionStorageKey,
  putGameRuleGenerationSessionDocument
} from "../services/gameRuleGenerationService";
import { openDraftInHost } from "../services/hostDocumentService";
import { valueForWord } from "../services/gameRuleFieldDisplay";
import { exportCurrentDocumentAsUint8Array } from "../services/gameRuleWordService";
import type { GameRuleFieldType, GameRuleWizardField } from "../types/gameRuleTemplate";
import { officeErrorToLogObject } from "../utils/officeJsDebugLog";

const LOG_PREFIX = "[GameRuleGenerate]";

const DUPLICATE_DOCUMENT_NAME_MESSAGE =
  "A document with this name already exists. Please choose a different name.";

const WORD_DEBOUNCE_MS = 300;

type StoredPayload = {
  readonly draftStoredFileId: string;
  readonly fields: GameRuleWizardField[];
};

export const GameRuleGeneratePage = () => {
  const { templateId, sessionId } = useParams<{ templateId: string; sessionId: string }>();
  const navigate = useNavigate();
  const [fields, setFields] = useState<GameRuleWizardField[]>([]);
  const [documentName, setDocumentName] = useState("");
  const [valuesByKey, setValuesByKey] = useState<Record<string, string>>({});
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [wordBanner, setWordBanner] = useState<string | null>(null);
  const [loadPhase, setLoadPhase] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [saving, setSaving] = useState(false);
  const [duplicateName, setDuplicateName] = useState(false);
  const [sharePointError, setSharePointError] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const fieldsRef = useRef<GameRuleWizardField[]>([]);
  const valuesRef = useRef<Record<string, string>>({});
  const debounceRef = useRef<number | undefined>(undefined);

  fieldsRef.current = fields;
  valuesRef.current = valuesByKey;

  const tid = templateId?.trim() ?? "";
  const sid = sessionId?.trim() ?? "";

  const flushWordDisplay = useCallback(async () => {
    const list = fieldsRef.current;
    if (!list.length) {
      console.info(LOG_PREFIX, "flushWordDisplay skip (no fields)");
      return;
    }
    const display: Record<string, string> = {};
    for (const f of list) {
      display[f.key.toUpperCase()] = valueForWord(f, valuesRef.current[f.key] ?? "");
    }
    console.info(LOG_PREFIX, "flushWordDisplay start", {
      fieldCount: list.length,
      keysSample: list.map((f) => f.key).slice(0, 24)
    });
    await applyTaggedPlaceholderDisplayValues(display);
    console.info(LOG_PREFIX, "flushWordDisplay ok");
  }, []);

  const scheduleWordFlush = useCallback(() => {
    if (debounceRef.current !== undefined) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = undefined;
      void flushWordDisplay()
        .then(() => setWordBanner(null))
        .catch((e: unknown) => {
          console.warn(LOG_PREFIX, "scheduleWordFlush → flushWordDisplay failed", officeErrorToLogObject(e));
          setWordBanner(e instanceof Error ? e.message : "Word could not update the document.");
        });
    }, WORD_DEBOUNCE_MS);
  }, [flushWordDisplay]);

  useEffect(
    () => () => {
      if (debounceRef.current !== undefined) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = undefined;
      }
    },
    []
  );

  useEffect(() => {
    if (!tid || !sid) {
      setLoadPhase("error");
      setError("Missing template or session.");
      return;
    }
    const ac = new AbortController();
    const { signal } = ac;
    setLoadPhase("loading");
    setError(null);
    void (async () => {
      try {
        console.info(LOG_PREFIX, "bootstrap start", { templateId: tid, sessionId: sid });
        const raw = sessionStorage.getItem(generationSessionStorageKey(sid));
        if (!raw) {
          throw new Error("Session expired or missing. Start again from the template list.");
        }
        const payload = JSON.parse(raw) as StoredPayload;
        if (!payload.draftStoredFileId || !Array.isArray(payload.fields)) {
          throw new Error("Invalid session data. Start again from the template list.");
        }
        console.info(LOG_PREFIX, "session payload ok", {
          draftStoredFileId: payload.draftStoredFileId,
          fieldCount: payload.fields.length,
          fieldKeysSample: payload.fields.map((f) => f.key).slice(0, 24)
        });
        setFields(payload.fields);
        const init: Record<string, string> = {};
        for (const f of payload.fields) {
          init[f.key] = "";
        }
        setValuesByKey(init);
        valuesRef.current = init;
        fieldsRef.current = payload.fields;
        setTitle("Generate document");

        console.info(LOG_PREFIX, "getDraftFileBase64 …", { storedFileId: payload.draftStoredFileId });
        const base64 = await getDraftFileBase64(payload.draftStoredFileId);
        if (signal.aborted) {
          return;
        }
        console.info(LOG_PREFIX, "getDraftFileBase64 ok", { base64Length: base64.length });

        console.info(LOG_PREFIX, "openDraftInHost …");
        await openDraftInHost(base64);
        if (signal.aborted) {
          return;
        }
        console.info(LOG_PREFIX, "openDraftInHost ok");
        // Let Word finish layout after insertFileFromBase64 before mutating the story (reduces GeneralException).
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, 400);
        });
        if (signal.aborted) {
          return;
        }
        console.info(LOG_PREFIX, "post-open delay 400ms done");
        const keys = payload.fields.map((f) => f.key);
        console.info(LOG_PREFIX, "migrateBareDoubleBracketTokensToContentControls …", {
          keyCount: keys.length,
          keysSample: keys.slice(0, 24)
        });
        await migrateBareDoubleBracketTokensToContentControls(keys, { signal });
        if (signal.aborted) {
          return;
        }
        console.info(LOG_PREFIX, "migrateBareDoubleBracketTokensToContentControls ok");
        const display: Record<string, string> = {};
        for (const f of payload.fields) {
          display[f.key.toUpperCase()] = valueForWord(f, "");
        }
        console.info(LOG_PREFIX, "applyTaggedPlaceholderDisplayValues (initial empty) …", {
          displayKeyCount: Object.keys(display).length
        });
        await applyTaggedPlaceholderDisplayValues(display);
        if (signal.aborted) {
          return;
        }
        console.info(LOG_PREFIX, "applyTaggedPlaceholderDisplayValues (initial) ok");
        setLoadPhase("ready");
        console.info(LOG_PREFIX, "bootstrap complete → loadPhase ready");
      } catch (e: unknown) {
        if (signal.aborted || (e instanceof DOMException && e.name === "AbortError")) {
          console.info(LOG_PREFIX, "bootstrap aborted (remount or navigation)", { templateId: tid, sessionId: sid });
          return;
        }
        console.error(LOG_PREFIX, "bootstrap failed", {
          message: e instanceof Error ? e.message : String(e),
          error: officeErrorToLogObject(e)
        });
        setLoadPhase("error");
        setError(e instanceof Error ? e.message : "Failed to open generation session.");
      }
    })();
    return () => {
      ac.abort();
    };
  }, [tid, sid]);

  const onChangeKey = (key: string, raw: string) => {
    setTouched((t) => ({ ...t, [key]: true }));
    setDuplicateName(false);
    setSharePointError(false);
    setValuesByKey((prev) => {
      const next = { ...prev, [key]: raw };
      valuesRef.current = next;
      return next;
    });
    scheduleWordFlush();
  };

  const onChangeDocumentName = (v: string) => {
    setDocumentName(v);
    setTouched((t) => ({ ...t, __docName: true }));
    setDuplicateName(false);
    setSharePointError(false);
  };

  const missingRequired = (): string[] => {
    const miss: string[] = [];
    if (!documentName.trim()) {
      miss.push("__docName");
    }
    for (const f of fields) {
      if (!f.required) {
        continue;
      }
      const v = valuesByKey[f.key] ?? "";
      if (f.type === "checkbox") {
        continue;
      }
      if (!String(v).trim()) {
        miss.push(f.key);
      }
    }
    return miss;
  };

  const onSave = async () => {
    setTouched(
      Object.fromEntries([["__docName", true], ...fields.map((f) => [f.key, true] as const)])
    );
    const miss = missingRequired();
    if (miss.length > 0) {
      setError("Please complete all required fields.");
      return;
    }
    setSaving(true);
    setError(null);
    setDuplicateName(false);
    setSharePointError(false);
    try {
      console.info(LOG_PREFIX, "onSave start", { sessionId: sid, documentNameLen: documentName.trim().length });
      if (debounceRef.current !== undefined) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = undefined;
      }
      // Align refs with latest React state before the final Word flush (avoids rare stale reads).
      fieldsRef.current = fields;
      valuesRef.current = valuesByKey;
      await flushWordDisplay();
      console.info(LOG_PREFIX, "onSave after flushWordDisplay");
      await unwrapTaggedPlaceholderControlsPreserveContent(fields.map((f) => f.key));
      console.info(LOG_PREFIX, "onSave after unwrapTaggedPlaceholderControlsPreserveContent");
      const bytes = await exportCurrentDocumentAsUint8Array();
      console.info(LOG_PREFIX, "onSave exportCurrentDocumentAsUint8Array ok", { byteLength: bytes.length });
      const copy = Uint8Array.from(bytes);
      const blob = new Blob([copy], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      });
      console.info(LOG_PREFIX, "onSave putGameRuleGenerationSessionDocument …");
      await putGameRuleGenerationSessionDocument(sid, blob);
      console.info(LOG_PREFIX, "onSave put ok; commit …");
      await commitGameRuleGenerationSession(sid, documentName.trim());
      console.info(LOG_PREFIX, "onSave commit ok");
      sessionStorage.removeItem(generationSessionStorageKey(sid));
      navigate("/prizelogic/listing");
    } catch (e: unknown) {
      console.error(LOG_PREFIX, "onSave failed", officeErrorToLogObject(e));
      const msg = e instanceof Error ? e.message : "Save failed.";
      if (e instanceof ApiError && e.status === 422 && msg.includes("already exists")) {
        setDuplicateName(true);
      } else if (e instanceof ApiError && e.status === 502) {
        setSharePointError(true);
        setError("Could not save the document to SharePoint. Please try again.");
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f: GameRuleWizardField) => {
    const v = valuesByKey[f.key] ?? "";
    const invalid = touched[f.key] && f.required && f.type !== "checkbox" && !String(v).trim();
    const commonLabel = (
      <label className="block text-sm font-medium text-foreground mb-1" htmlFor={`grg-${f.id}`}>
        {f.label}
        {f.required ? <span className="text-destructive"> *</span> : null}
      </label>
    );
    const baseInput = `w-full min-w-0 rounded-md border px-3 py-2 text-sm ${
      invalid ? "border-destructive ring-1 ring-destructive/30" : "border-input bg-background"
    }`;

    switch (f.type as GameRuleFieldType) {
      case "text_area":
        return (
          <div>
            {commonLabel}
            <textarea
              id={`grg-${f.id}`}
              className={`${baseInput} min-h-[88px]`}
              value={v}
              onChange={(e) => onChangeKey(f.key, e.target.value)}
            />
          </div>
        );
      case "number_input":
        return (
          <div>
            {commonLabel}
            <input
              id={`grg-${f.id}`}
              type="number"
              className={baseInput}
              value={v}
              onChange={(e) => onChangeKey(f.key, e.target.value)}
            />
          </div>
        );
      case "date_picker":
        return (
          <div>
            {commonLabel}
            <input
              id={`grg-${f.id}`}
              type="date"
              className={baseInput}
              value={v}
              onChange={(e) => onChangeKey(f.key, e.target.value)}
            />
          </div>
        );
      case "checkbox":
        return (
          <div className="flex items-center gap-2">
            <input
              id={`grg-${f.id}`}
              type="checkbox"
              checked={v === "true" || v === "1"}
              onChange={(e) => onChangeKey(f.key, e.target.checked ? "true" : "false")}
            />
            <label className="text-sm font-medium text-foreground" htmlFor={`grg-${f.id}`}>
              {f.label}
              {f.required ? <span className="text-destructive"> *</span> : null}
            </label>
          </div>
        );
      case "radio":
        return (
          <div className="space-y-2 min-w-0">
            {commonLabel}
            <div className="space-y-1">
              {f.options.map((o) => (
                <label key={o.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name={`grg-${f.id}`}
                    value={o.value}
                    checked={v === o.value}
                    onChange={() => onChangeKey(f.key, o.value)}
                  />
                  {o.label || o.value}
                </label>
              ))}
            </div>
          </div>
        );
      case "select":
        return (
          <div className="min-w-0">
            {commonLabel}
            <select
              id={`grg-${f.id}`}
              className={baseInput}
              value={v}
              onChange={(e) => onChangeKey(f.key, e.target.value)}
            >
              <option value="">—</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label || o.value}
                </option>
              ))}
            </select>
          </div>
        );
      default:
        return (
          <div className="min-w-0">
            {commonLabel}
            <input
              id={`grg-${f.id}`}
              type="text"
              className={baseInput}
              value={v}
              onChange={(e) => onChangeKey(f.key, e.target.value)}
            />
          </div>
        );
    }
  };

  const docNameInvalid = touched.__docName && !documentName.trim();

  return (
    <div className="mx-auto max-w-2xl space-y-4 pb-40">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Generate document</h2>
          <p className="text-sm text-muted-foreground mt-1">{title}</p>
        </div>
        <Link
          to="/prizelogic/library"
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm font-medium text-primary hover:bg-accent"
        >
          Back to list
        </Link>
      </div>

      {wordBanner ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
          {wordBanner}
        </div>
      ) : null}

      {error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
          {sharePointError ? (
            <button type="button" className="ml-2 font-semibold underline" onClick={() => void onSave()}>
              Retry save
            </button>
          ) : null}
        </div>
      ) : null}

      {duplicateName ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {DUPLICATE_DOCUMENT_NAME_MESSAGE}
        </div>
      ) : null}

      {loadPhase === "loading" ? (
        <p className="text-sm text-muted-foreground">Opening working copy…</p>
      ) : null}

      {loadPhase === "ready" ? (
        <div className="space-y-6 rounded-lg border border-[var(--border)] bg-card p-4 shadow-sm">
          <div className="min-w-0">
            <label
              className="block text-sm font-medium text-foreground mb-1"
              htmlFor="grg-doc-name"
            >
              Document Name <span className="text-destructive">*</span>
            </label>
            <input
              id="grg-doc-name"
              type="text"
              className={`w-full min-w-0 rounded-md border px-3 py-2 text-sm ${
                docNameInvalid ? "border-destructive ring-1 ring-destructive/30" : "border-input bg-background"
              }`}
              value={documentName}
              onChange={(e) => onChangeDocumentName(e.target.value)}
              placeholder="Shown in the generated documents list"
            />
          </div>
          {fields.map((f) => (
            <div key={f.id} className="min-w-0 space-y-1">
              {renderField(f)}
            </div>
          ))}
        </div>
      ) : null}

      {loadPhase === "ready" ? (
        <div className="fixed bottom-0 left-0 right-0 border-t border-[var(--border)] bg-background/95 p-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="mx-auto max-w-2xl">
            <button
              type="button"
              disabled={saving}
              className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              onClick={() => void onSave()}
            >
              {saving ? "Saving…" : "Save generated document"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
